import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import { 
  Close as CloseIcon,
  Add as AddIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

interface CreatePPPSecretDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateSecret: (secret: {
    username: string;
    password: string;
    profile: string;
  }) => void;
  routerId: string;
  routerName?: string;
  isLoading?: boolean;
}

const CreatePPPSecretDialog: React.FC<CreatePPPSecretDialogProps> = ({
  open,
  onClose,
  onCreateSecret,
  routerId,
  routerName,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    profile: 'default'
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const handleInputChange = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username wajib diisi';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username minimal 3 karakter';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      newErrors.username = 'Username hanya boleh berisi huruf, angka, underscore, dan dash';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password wajib diisi';
    } else if (formData.password.length < 4) {
      newErrors.password = 'Password minimal 4 karakter';
    }
    
    if (!formData.profile.trim()) {
      newErrors.profile = 'Profile wajib diisi';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onCreateSecret(formData);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        username: '',
        password: '',
        profile: 'default'
      });
      setErrors({});
      onClose();
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({
      ...prev,
      password: password
    }));
  };

  const generateUsernameFromCustomer = () => {
    // This could be improved by getting customer data from parent
    const timestamp = Date.now().toString().slice(-4);
    const username = `user_${timestamp}`;
    setFormData(prev => ({
      ...prev,
      username: username
    }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            <Typography variant="h6" component="div">
              Buat PPP Secret Baru
            </Typography>
          </Box>
          <IconButton 
            onClick={handleClose}
            size="small"
            sx={{ color: 'white' }}
            disabled={isLoading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              PPP Secret baru akan dibuat di router <strong>{routerName || 'yang dipilih'}</strong> dan disimpan ke database
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Username Field */}
            <Box>
              <TextField
                label="Username"
                value={formData.username}
                onChange={handleInputChange('username')}
                fullWidth
                required
                placeholder="Masukkan username untuk PPP Secret"
                error={!!errors.username}
                helperText={errors.username || 'Username untuk login PPP (minimal 3 karakter)'}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <Button
                      size="small"
                      onClick={generateUsernameFromCustomer}
                      disabled={isLoading}
                      sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                    >
                      🎲 Generate
                    </Button>
                  )
                }}
              />
            </Box>

            {/* Password Field */}
            <Box>
              <TextField
                label="Password"
                type="text"
                value={formData.password}
                onChange={handleInputChange('password')}
                fullWidth
                required
                placeholder="Masukkan password untuk PPP Secret"
                error={!!errors.password}
                helperText={errors.password || 'Password untuk login PPP (minimal 4 karakter)'}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <Button
                      size="small"
                      onClick={generateRandomPassword}
                      disabled={isLoading}
                      sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                    >
                      🔐 Generate
                    </Button>
                  )
                }}
              />
            </Box>

            {/* Profile Field */}
            <Box>
              <TextField
                label="Profile"
                value={formData.profile}
                onChange={handleInputChange('profile')}
                fullWidth
                required
                placeholder="Masukkan nama profile (contoh: default, premium)"
                error={!!errors.profile}
                helperText={errors.profile || 'Nama profile PPP yang akan digunakan'}
                disabled={isLoading}
              />
            </Box>

            <Divider />

            {/* Info Section */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon fontSize="small" />
                Informasi PPP Secret
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                • Username dan password akan digunakan untuk autentikasi PPP
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                • Profile menentukan konfigurasi bandwidth dan rules yang akan diterapkan
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                • Data akan disimpan di MikroTik dan database secara bersamaan
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleClose}
            variant="outlined"
            color="inherit"
            disabled={isLoading}
          >
            Batal
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={isLoading || !formData.username.trim() || !formData.password.trim() || !formData.profile.trim()}
            startIcon={isLoading ? <CircularProgress size={16} /> : <AddIcon />}
            sx={{
              background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #388E3C 30%, #4CAF50 90%)',
              }
            }}
          >
            {isLoading ? 'Membuat...' : 'Buat PPP Secret'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreatePPPSecretDialog;
